import React, {
	useCallback,
	useEffect,
	useMemo,
	useState
} from 'react';
import {
	Divider,
	Segment
} from 'semantic-ui-react';

import { useAgencyState } from '../../../contexts/AgenciesContext';
import { useAuthState } from '../../../contexts/AuthContext';

import HelpModal from '../../Shared/HelpModal';
import multiWordFilterFn from '../../Table/filterFunctions/multiWordFilterFn';
import PermissionsWrapper from '../../PermissionsWrapper';
import ProgramsMobileColumn from './ProgramsMobileColumn';
import ProgramsModal from './ProgramsModal';
import Table from '../../Table';
import TableRowActions from '../../Table/Cells/TableRowActions';

import styles from '../agency-portal.module.css';

const Programs = () => {
	const { agency } = useAgencyState();
	const { user: { permissions } } = useAuthState();

	const [ program, setProgram ] = useState( null );
	const [ programsModal, setProgramsModal ] = useState( false );
	const [ width, setWidth ] = useState( window.innerWidth );

	useEffect( () => {
		const handleResize = () => setWidth( window.innerWidth );

		window.addEventListener( 'resize', handleResize );

		return () => window.removeEventListener( 'resize', handleResize );
	}, [] );

	const closeProgramsModal = useCallback( () => {
		setProgramsModal( false );
	}, [] );

	// Data Table
	const programsMobileColumn = useCallback( ( { row } ) => <ProgramsMobileColumn row={row.original} />, [] );

	const onView = useCallback( program => {
		setProgram( program );
		setProgramsModal( true );
	}, [] );

	const tableRowActionsCell = useCallback( ( { row } ) => {
		const actions = [
			{
				action: onView,
				componentProps: { color: 'pink' },
				icon: 'search',
				permission: permissions.agencyViewPrograms.canExecute,
				text: 'View Program'
			}
		];

		return (
			<TableRowActions
				actions={ actions }
				row={ row.original }
			/>
		);
	}, [
		onView,
		permissions.agencyViewPrograms.canExecute
	] );

	const columns = useMemo( () => [
		{
			accessor: 'name',
			Header: 'Name',
			filter: multiWordFilterFn,
			minWidth: 60
		},
		{
			accessor: 'submissionStatus',
			Header: 'Status',
			filter: multiWordFilterFn,
			minWidth: 60
		}
	], [ ] );

	const mobileColumns = useMemo( () => [
		{
			id: 'mobile_column',
			accessor: row => `${ row.name }${ row.submissionStatus }`,
			Header: 'Programs',
			filter: multiWordFilterFn,
			Cell: programsMobileColumn,
			minWidth: 60
		}

	], [ programsMobileColumn ] );

	const actionColumn = useMemo( () => ( {
		id: 'id_actions',
		Cell: tableRowActionsCell,
		Header: 'View',
		disableFilters: true,
		disableResizing: true,
		width: 100,
		minWidth: 100,
		maxWidth: 100
	} ), [ tableRowActionsCell ] );

	const renderColumns = useMemo(
		() => permissions.agencyViewPrograms.isAvailable
			? [
				...width < 767 ? mobileColumns : columns,
				actionColumn
			]
			: width < 767 ? mobileColumns : columns,
		[
			actionColumn,
			columns,
			mobileColumns,
			permissions,
			width
		]
	);

	return (
		<>
			<div className={ styles.container } >
				<header className={ styles.pageHeader }>
					<h2>
						Programs
						<HelpModal
							id={ 6 }
							triggerStyles={ { verticalAlign: 'top' } }
						/>
					</h2>
				</header>
				<Segment basic as="div" className={ styles.content }>
					<PermissionsWrapper
						alt={ <p>Access denied.</p> }
						permission={ permissions.agencyListPrograms.canExecute }
					>
						<Table
							columns={ renderColumns }
							data={ agency.programs }
							showCount={ agency.programs.length > 10 }
							showEmptyRows={ false }
							showPagination={ agency.programs.length > 10 }
							style={ { overflowX: 'auto' } }
						/>
					</PermissionsWrapper>
				</Segment>
			</div>
			<Divider hidden />
			<ProgramsModal
				closeModal={ closeProgramsModal }
				open={ programsModal }
				program={ program }
			/>
		</>
	);
};

export default Programs;
